import type { JSX } from 'react';
import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { defaultAuthenticatedPath } from '../lib/defaultAuthenticatedPath';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * `/login` — the sign-in address a brand's website links its Login button to
 * (PD-16862).
 *
 * Home (`/`) alone is not a sign-in page: on a brand that allows guest browsing it
 * is the guest shop, so a website's Login landed visitors there with no sign-in in
 * sight. A website also cannot tell whether its visitor is already signed in here,
 * because the session lives in this storefront's own browser storage on its own
 * domain. This address answers both:
 * - Signed in: straight to the storefront's start page.
 * - Signed out: home, with the sign-in form open over it (the same as the Register
 *   page's Sign in link). Closing the form leaves a guest-browsing visitor in the
 *   guest shop, which is how guests get in now that the websites have no separate
 *   Guest Access button.
 *
 * Rendered inside PortalLayout, which holds every child route until the saved session
 * has been restored, so a signed-in customer is never mistaken for a signed-out one.
 */
export function LoginPage(): JSX.Element | null {
	const navigate = useNavigate();
	const { isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const openLoginModal = useLoginModalStore((s) => s.open);

	useEffect(() => {
		if (isAuthenticated) {
			return;
		}
		openLoginModal();
		void navigate('/', { replace: true });
	}, [isAuthenticated, navigate, openLoginModal]);

	if (isAuthenticated) {
		return (
			<Navigate
				to={defaultAuthenticatedPath(display.defaultAuthenticatedView)}
				replace
			/>
		);
	}

	return null;
}
